import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { Libraries, useJsApiLoader } from "@react-google-maps/api";
import { useState } from "react";

import { UserAddress } from "../../auth/AuthContext";
import AutocompleteInput from "../../LocationsModule/PlaceAutocompleteInput";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useDeliveryAddress } from "../DeliveryAddressProvider";

interface Props {
    onDone: () => void;
}

const googleMapsLibraries: Libraries = ["places"];

const AddAddressFlow = ({ onDone }: Props) => {
    const { addAddress } = useDeliveryAddress();
    const { theme } = useCustomTheme();

    const [label, setLabel] = useState("Home");
    const [instructions, setInstructions] = useState("");
    const [addressLine, setAddressLine] = useState("");
    const [coords, setCoords] = useState<{ lat: number; lng: number; } | null>(
        null,
    );
    const [saving, setSaving] = useState(false);

    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: googleMapsLibraries,
    });

    const handleSave = async () => {
        if (!coords || !addressLine) return;

        setSaving(true);

        try {
            const newAddress: UserAddress = {
                id: crypto.randomUUID(),
                label,
                addressLine,
                latitude: coords.lat,
                longitude: coords.lng,
                instructions,
                isCurrent: true,
                createdAt: Date.now(),
            };

            await addAddress(newAddress);
            onDone();
        } finally {
            setSaving(false);
        }
    };

    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <Flex
                minH="120px"
                align="center"
                justify="center"
                direction="column"
                gap={2}
                textAlign="center"
            >
                <Text color={theme.text} fontWeight="700">
                    Google Maps API key missing
                </Text>
                <Text color={theme.textMuted} fontSize="sm">
                    Please check REACT_APP_GOOGLE_MAPS_API_KEY in your environment file.
                </Text>
            </Flex>
        );
    }

    if (!isLoaded) {
        return (
            <Flex minH="120px" align="center" justify="center" gap={3}>
                <Spinner size="sm" color={theme.primary} />
                <Text color={theme.textMuted} fontSize="sm">
                    Loading address autocomplete...
                </Text>
            </Flex>
        );
    }

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel color={theme.text}>Delivery address</FormLabel>
                <AutocompleteInput
                    placeholder="Search delivery address"
                    value={addressLine}
                    onPlaceSelected={setAddressLine}
                    onLocationSelected={setCoords}
                />
            </FormControl>

            <FormControl>
                <FormLabel color={theme.text}>Label</FormLabel>
                <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Home, Work, Shop…"
                    bg={theme.inputBg}
                    color={theme.text}
                    borderColor={theme.inputBorder}
                    _placeholder={{ color: theme.inputPlaceholder }}
                    _hover={{ borderColor: theme.borderStrong }}
                    _focus={{
                        borderColor: theme.inputBorderFocus,
                        boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                    }}
                />
            </FormControl>

            <FormControl>
                <FormLabel color={theme.text}>Delivery instructions</FormLabel>
                <Input
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Gate color, landmarks, call on arrival…"
                    bg={theme.inputBg}
                    color={theme.text}
                    borderColor={theme.inputBorder}
                    _placeholder={{ color: theme.inputPlaceholder }}
                    _hover={{ borderColor: theme.borderStrong }}
                    _focus={{
                        borderColor: theme.inputBorderFocus,
                        boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                    }}
                />
            </FormControl>

            <Button
                mt={4}
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                isDisabled={!coords || !addressLine}
                isLoading={saving}
                onClick={handleSave}
            >
                Confirm address
            </Button>
        </VStack>
    );
};

export default AddAddressFlow;